/** @file This file contains the extended Course model and related searcher implementations. */
import * as api from '@codegrade/api';
import { assert } from '@codegrade/utils';
import client from '@/client';
import { CoursePermissions as CPerm } from '@/permissions';
import { CoursesStore, TenantsStore } from '@/store';
/** A pre-filtered list of all permissions that grant course management rights. */
const canManageCoursePermissions = Object.freeze(Object.values(CPerm).filter(perm => perm.manageCourse));
/** A pre-filtered list of all permissions that grant assignment management rights. */
const canManageAssignmentPermissions = Object.freeze(Object.values(CPerm).filter(perm => perm.manageAssignment));
/**
 * An extended client-side model for a Course.
 *
 * This class wraps the API's `ExtendedCourse` data and enriches it with
 * authorization data and convenience methods for permission checking.
 */
export class Course extends api.ExtendedCourse {
    authorization;
    /**
     * @param data The base course data from the API.
     * @param authorization The authorization and permission data for the current user in this course.
     */
    constructor(data, authorization) {
        super(data);
        this.authorization = authorization;
        Object.freeze(this);
    }
    /** @returns `true` if the course is connected to an LTI provider. */
    get isLTI() {
        return this.ltiProvider.isJust();
    }
    /** @returns `true` if the course's payment status is settled (e.g., successful or not needed). */
    get hasBeenPaid() {
        const payment = this.authorization.paymentStatus;
        switch (payment.tag) {
            case 'access-expired':
            case 'payment-required':
            case 'transaction-pending':
            case 'access-revoked':
                return false;
            case 'access-granted':
            case 'no-payment-required':
                return true;
            default:
                return assert.AssertionError.assertNever(payment);
        }
    }
    /**
     * Determines whether the current user can view the members of any course sections.
     *
     * @returns `true` if the user has permissions to view all sections or their own section members.
     */
    canViewSectionMembers() {
        if (this.hasPermission(CPerm.can_view_course_sections) &&
            this.hasPermission(CPerm.can_list_course_users)) {
            return true;
        }
        if (this.hasPermission(CPerm.can_view_own_course_section_members)) {
            return true;
        }
        return false;
    }
    /**
     * Checks if the current user has a specific permission for this course.
     *
     * @param permission The permission to check for.
     * @returns `true` if the user holds the specified permission.
     */
    hasPermission(permission) {
        return this.authorization.permissions[permission.value];
    }
    /** @returns `true` if the current user has any course management permissions. */
    get canManage() {
        return canManageCoursePermissions.some(perm => this.hasPermission(perm));
    }
    /** @returns `true` if the current user has any assignment management permissions. */
    get canManageAssignment() {
        return canManageAssignmentPermissions.some(perm => perm.manageAssignment && this.hasPermission(perm));
    }
    /** @returns `true` if the current user can create new assignments in this course. */
    get canCreateAssignments() {
        return this.hasPermission(CPerm.can_create_assignment);
    }
    /** @returns `true` if the current user has only student-level permissions in this course. */
    get isStudent() {
        return !(this.canManage || this.canManageAssignment);
    }
    /** @returns `true` if the course is archived. */
    get isArchived() {
        return this.state === 'archived';
    }
    /** @returns The tenant for this course, fetched from the store. */
    get tenant() {
        return this.tenantId.chain(TenantsStore.getTenant());
    }
    /** @returns `true` if the current user has the necessary permissions to copy content into this course. */
    get canBeCopiedInto() {
        return (this.hasPermission(CPerm.can_edit_group_set) &&
            this.hasPermission(CPerm.can_manage_course_snippets) &&
            this.hasPermission(CPerm.can_create_assignment));
    }
}
/** A searcher implementation for fetching all available courses. */
export class CourseSearcher {
    /**
     * Searches for courses via the API.
     *
     * @param data The search payload.
     * @returns A promise resolving with the search results and a potential next page token.
     */
    async search(data) {
        const response = await client.p.course.getAll({
            q: data.query,
            nextToken: data.nextToken,
        });
        return {
            data: response.data,
            nextToken: response.nextToken,
        };
    }
    /**
     * Loads a page of courses into the store.
     *
     * @param data The load payload.
     */
    async loadItems(data) {
        const toLoad = data.tag === 'first-page' ? 'first-page' : 'next-page';
        await CoursesStore.loadCourses({ toLoad });
    }
    /** @returns The current paginated state of all courses from the store. */
    getItems() {
        return CoursesStore.allCourses();
    }
}
